import PropTypes from 'prop-types';
import { useState, useMemo, useRef, useCallback } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';

// @mui
import {
  Box,
  List,
  ListItemText,
  Tooltip,
  Typography,
  Collapse,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

//
import { StyledNavItem, StyledNavItemIcon } from './styles';
import { hasNavPermission } from '../../utils/hasPermission';
import useServerPermissions, { mergePermissions } from '../../hooks/useServerPermissions';
import { useSelector } from 'react-redux';

// ââ LocalStorage key ââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ
const STORAGE_KEY = 'sfm_sidebar_group_order';

function loadSavedOrder() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveOrder(order) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
  } catch {}
}

// ââ Helpers âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

/** Group the flat navConfig array into sections: { subheader, items[] } */
function groupBySubheader(data) {
  const groups = [];
  let current = null;
  for (const item of data) {
    if (item.subheader) {
      current = { subheader: item.subheader, items: [] };
      groups.push(current);
    } else if (current) {
      current.items.push(item);
    } else {
      if (!groups.length || groups[groups.length - 1].subheader) {
        groups.push({ subheader: null, items: [item] });
      } else {
        groups[groups.length - 1].items.push(item);
      }
    }
  }
  return groups;
}

/** Apply saved order to groups array */
function applyOrder(groups, savedOrder) {
  if (!savedOrder || !savedOrder.length) return groups;

  const byName = {};
  groups.forEach((g) => {
    if (g.subheader) byName[g.subheader] = g;
  });

  const ordered = [];

  // First, place groups in saved order
  savedOrder.forEach((name) => {
    if (byName[name]) {
      ordered.push(byName[name]);
      delete byName[name];
    }
  });

  // Append any new groups not in saved order
  groups.forEach((g) => {
    if (!g.subheader) ordered.push(g); // ungrouped items
    else if (byName[g.subheader]) ordered.push(g);
  });

  return ordered;
}

// ââ NavSection ââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

NavSection.propTypes = {
  data: PropTypes.array,
};

export default function NavSection({ data = [], ...other }) {
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: tokenPermissions } = loginUser.decodedToken;
  // Items marked serverResolved also honour company-level overrides (display only).
  const needsServer = useMemo(() => data.some((i) => i.serverResolved && i.requiredPermission
    && !hasNavPermission(tokenPermissions || [], i.requiredPermission)), [data, tokenPermissions]);
  const server = useServerPermissions(needsServer);
  const { pathname } = useLocation();

  const baseGroups = useMemo(() => groupBySubheader(data), [data]);

  // Apply saved order
  const [groupOrder, setGroupOrder] = useState(() => loadSavedOrder());
  const groups = useMemo(() => applyOrder(baseGroups, groupOrder), [baseGroups, groupOrder]);

  // Determine which group contains the active route
  const activeGroupIndex = useMemo(() => {
    for (let i = 0; i < groups.length; i++) {
      if (groups[i].items.some((item) => pathname.startsWith(item.path))) return i;
    }
    return 0;
  }, [groups, pathname]);

  // Collapse state
  const [openMap, setOpenMap] = useState(() => {
    const map = {};
    groups.forEach((_, i) => {
      map[i] = i === activeGroupIndex;
    });
    return map;
  });

  const toggle = (idx) => {
    setOpenMap((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // ââ Drag-and-drop state ââââââââââââââââââââââââââââââââââââââââââââââââââ
  const dragIdx = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleDragStart = useCallback((e, idx) => {
    dragIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image slightly transparent
    if (e.target) {
      e.dataTransfer.setDragImage(e.target, 20, 16);
    }
  }, []);

  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIdx(idx);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIdx(null);
  }, []);

  const handleDrop = useCallback(
    (e, dropIdx) => {
      e.preventDefault();
      setDragOverIdx(null);
      const fromIdx = dragIdx.current;
      if (fromIdx === null || fromIdx === dropIdx) return;

      // Reorder the groups
      const reordered = [...groups];
      const [moved] = reordered.splice(fromIdx, 1);
      reordered.splice(dropIdx, 0, moved);

      // Remap open state
      const newOpenMap = {};
      reordered.forEach((g, i) => {
        const oldIdx = groups.indexOf(g);
        newOpenMap[i] = openMap[oldIdx] !== false;
      });
      setOpenMap(newOpenMap);

      // Persist order
      const newOrder = reordered.filter((g) => g.subheader).map((g) => g.subheader);
      setGroupOrder(newOrder);
      saveOrder(newOrder);
      dragIdx.current = null;
    },
    [groups, openMap]
  );

  const handleDragEnd = useCallback(() => {
    dragIdx.current = null;
    setDragOverIdx(null);
  }, []);

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {groups.map((group, idx) => {
          // Filter items by permission
          const visibleItems = group.items.filter((item) => {
            const itemPermissions = item.serverResolved ? mergePermissions(tokenPermissions, server.permissions) : (tokenPermissions || []);
            if (item.requiredPermission && !hasNavPermission(itemPermissions, item.requiredPermission)) {
              return false;
            }
            return true;
          });

          // Don't render the group if no visible items
          if (group.subheader && visibleItems.length === 0) return null;

          const isOpen = openMap[idx] !== false;

          // Groups without a subheader
          if (!group.subheader) {
            return visibleItems.map((item) => (
              <NavItem key={item.title} item={item} />
            ));
          }

          const isDragOver = dragOverIdx === idx;

          return (
            <Box
              key={group.subheader}
              sx={{
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                ...(isDragOver && {
                  '&::before': {
                    content: '""',
                    display: 'block',
                    height: 2,
                    backgroundColor: '#007AFF',
                    borderRadius: 1,
                    mx: 1.5,
                    mb: 0.5,
                  },
                }),
              }}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, idx)}
            >
              {/* Collapsible + draggable subheader */}
              <Box
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragEnd={handleDragEnd}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'grab',
                  px: 1.5,
                  pt: 2.5,
                  pb: 0.5,
                  userSelect: 'none',
                  borderRadius: 1,
                  '&:active': { cursor: 'grabbing' },
                  '&:hover': {
                    backgroundColor: 'rgba(120,120,128,0.06)',
                  },
                  '&:hover .drag-handle': {
                    opacity: 1,
                  },
                }}
              >
                {/* Drag handle \u2014 visible on hover */}
                <DragIndicatorIcon
                  className="drag-handle"
                  sx={{
                    fontSize: 14,
                    color: 'text.disabled',
                    opacity: 0,
                    transition: 'opacity 0.15s ease',
                    mr: 0.5,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  variant="overline"
                  onClick={() => toggle(idx)}
                  sx={{
                    flex: 1,
                    display: 'block',
                    color: 'text.disabled',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    letterSpacing: 1.0,
                    textTransform: 'uppercase',
                    lineHeight: 1.5,
                    cursor: 'pointer',
                  }}
                >
                  {group.subheader}
                </Typography>

                <ExpandMoreIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(idx);
                  }}
                  sx={{
                    fontSize: 16,
                    color: 'text.disabled',
                    transition: 'transform 0.25s ease',
                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                />
              </Box>

              {/* Collapsible items */}
              <Collapse in={isOpen} timeout={200} unmountOnExit={false}>
                {visibleItems.map((item) => (
                  <NavItem key={item.title} item={item} />
                ))}
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Box>
  );
}

// ââ NavItem âââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââââ

NavItem.propTypes = {
  item: PropTypes.object,
};

function NavItem({ item }) {
  const { title, path, icon, info, comingSoon, external } = item;

  // Respect the app preferences toggle for tooltips
  const tooltipsEnabled = localStorage.getItem('sfm_tooltips_enabled') !== 'false';
  const tooltipText = tooltipsEnabled
    ? comingSoon
      ? 'Coming Soon'
      : info || ''
    : '';

  // Coming-soon items render as non-clickable with a badge
  if (comingSoon) {
    return (
      <Tooltip title={tooltipText} placement="right" arrow>
        <StyledNavItem
          sx={{
            opacity: 0.5,
            cursor: 'default',
            '&:hover': { bgcolor: 'transparent' },
          }}
        >
          <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>
          <ListItemText disableTypography primary={title} />
          <Chip
            label="Soon"
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: 0.3,
              bgcolor: 'action.hover',
              color: 'text.disabled',
              mr: 0.5,
            }}
          />
        </StyledNavItem>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipText} placement="right" arrow>
      <StyledNavItem
        {...(external
          ? { component: 'a', href: path, target: '_blank', rel: 'noopener noreferrer' }
          : { component: RouterLink, to: path })}
        sx={{
          '&.active': {
            color: 'text.primary',
            bgcolor: 'action.selected',
            fontWeight: 'fontWeightBold',
          },
        }}
      >
        <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>
        <ListItemText disableTypography primary={title} />
      </StyledNavItem>
    </Tooltip>
  );
}
